<script setup lang="ts">
import { computed } from "vue";

import type { ComboButtonProps } from "@/dsComponents/buttons/types";

import Loader from "@/dsComponents/Loader.vue";
import ProgressCircle from "@/dsComponents/progress/ProgressCircle.vue";

const props = withDefaults(defineProps<ComboButtonProps>(), {
    size: "sm",
    noInnerPadding: false,
    variant: "ghost",
    showPlusWhenInactive: false,
});

const leftVisualIcon = computed(() => {
    if (props.iconOnly) {
        return props.iconOnly;
    }

    if (!props.iconLeft) {
        return undefined;
    }

    if (props.showPlusWhenInactive && !props.active) {
        return "plus";
    }

    return props.iconLeft;
});

const rightVisualIcon = computed(() => {
    if (props.iconRight) {
        return props.iconRight;
    }

    if (props.active) {
        if (props.iconOnly) {
            return undefined;
        }
        return "x";
    }

    return undefined;
});

const emit = defineEmits<{
    (name: "click", event: MouseEvent): void;
    (name: "right", event: MouseEvent): void;
}>();
</script>
<template>
    <button
        :class="[
            'ds-combo-button',
            variant,
            size,
            { iconOnly, placeholder, focus, error, readonly, active, 'no-hover': noHover, fullWidth, 'default-style': forceDefaultStyle },
        ]"
        :style="{ cursor }"
        @click="(e) => emit('click', e)"
        :disabled="readonly || loadingLeft || loadingRight"
    >
        <div v-if="leftVisualIcon || loadingLeft || progress" class="icon-chip-container">
            <div v-if="loadingLeft" class="loader">
                <Loader size="sm" full />
            </div>
            <ProgressCircle v-else-if="progress" v-bind="progress" />
            <i v-else :class="['ti', `ti-${leftVisualIcon}`, iconLeftColor]" :style="iconLeftStyle"></i>
        </div>

        <div v-if="!iconOnly && filterLabel" class="filter-label">
            <i v-if="filterLabel.icon" :class="`ti ti-${filterLabel.icon}`"></i>
            <span v-if="filterLabel.label" class="text-xs regular">{{ filterLabel.label }}</span>
        </div>
        <div class="vertical-separator" v-if="filterLabel && label"></div>
        <div v-if="!iconOnly" :class="['label-comboButton', size, { 'no-padding': noInnerPadding }]">
            <slot>
                <span v-if="label">{{ label }}</span>
            </slot>
        </div>
        <i v-if="rightVisualIcon" :class="`ti ti-${rightVisualIcon} combo-button-right-icon`" @click.stop="(e) => emit('right', e)"></i>
        <Loader v-if="loadingRight" size="sm" />
    </button>
</template>
<style lang="scss" scoped>
$size-sm: 28px;
$size-xs: 24px;

.ds-combo-button {
    display: flex;
    align-items: center;
    height: $size-sm;
    background-color: transparent;
    border-radius: 6px;
    text-overflow: ellipsis;
    max-width: 100%;

    &.fullWidth {
        width: 100%;
    }

    &:disabled {
        cursor: default;
    }

    color: $text-main-900;
    font-weight: 500;
    gap: 4px;
    @include font-size-13;

    &.error {
        color: var(--text-soft-400, #9297aa);
        border: 1px solid var(--red-base, #e51940);
        .ti {
            color: $red-base !important;
        }
    }

    &.readonly {
        cursor: default;
    }
    &:not(.placeholder) {
        .ti {
            font-size: 14px;
            color: $icon-sub-500;
        }
    }

    &:not(.iconOnly) {
        padding: 0 8px;
    }
    &.iconOnly {
        width: $size-sm;
        font-size: 14px;
    }

    &.xs {
        height: $size-xs;
        font-weight: 400;
        padding: 0 6px;
        gap: 4px;
        border-radius: 5px;
        @include font-size-12;
        &.iconOnly {
            width: $size-xs;
            font-size: 12px;
        }
        .ti {
            font-size: 12px;
        }
    }

    &.md {
        height: 36px;

        &.iconOnly {
            width: 36px;
            justify-content: center;
        }

        .ti {
            font-size: 18px;
        }
    }

    .icon-chip-container {
        display: flex;
        align-items: center;
        position: relative;
    }

    &.placeholder {
        color: $text-soft-400;
    }

    &.focus:not(:disabled):not(.error),
    &:hover:not(:disabled):not(.error):not(.no-hover) {
        color: $text-main-900;
        .ti {
            color: $text-main-900;
        }
    }

    &.stroke {
        border: 0.5px $stroke-soft-200 solid;
        background-color: $bg-white-0;

        &.error {
            border: 1px solid var(--red-base, #e51940);
        }

        &.focus:not(:disabled):not(.error),
        &:hover:not(:disabled):not(.no-hover):not(.error) {
            background-color: $neutral-50;
            border: 0.5px $stroke-sub-300 solid;
        }
    }
    &.ghost {
        &.focus:not(:disabled),
        &:hover:not(:disabled):not(.no-hover) {
            background-color: $neutral-100;
        }

        &.default-style:not(.focus):not(:hover):not(.error):not(.placeholder) {
            color: $text-sub-600;
            .ti {
                color: $text-sub-600;
            }
        }
    }

    &.dashed {
        border: 1px dashed var(--stroke-soft-200, #dbdfe6);
        background-color: var(--bg-white-0, #ffffff);

        &.active,
        &:active:not(:disabled) {
            border-style: solid;
            border-color: var(--stroke-sub-300, #acb1c3);
            background-color: var(--bg-lightest-50, #f6f7f9);
        }

        &.focus:not(:disabled),
        &:hover:not(:disabled):not(.no-hover) {
            border-color: var(--stroke-sub-300, #acb1c3);
            background-color: var(--bg-lightest-50, #f6f7f9);
        }
    }

    &.color {
        color: var(--vibrant-blue-blue-600, #3d69ff);
        border-radius: var(--radius-sm, 6px);
        border: 1px dashed var(--bg-white-0, #ffffff);
        background: linear-gradient(var(--bg-white-0, #ffffff) 0 0) padding-box, linear-gradient($gradient) border-box;

        .icon-chip-container {
            .ti {
                background: -moz-linear-gradient($gradient);
                background: -webkit-linear-gradient($gradient);
                background: linear-gradient($gradient);
                -webkit-background-clip: text;
                -moz-background-clip: text;
                background-clip: text;
                -webkit-text-fill-color: transparent;
            }
        }

        .ti {
            color: var(--vibrant-blue-blue-500, #6689ff);
        }

        &.active,
        &:active:not(:disabled) {
            color: var(--vibrant-blue-blue-600, #3d69ff);
            border: 1px solid transparent;
            background: linear-gradient(var(--blue-lightest, #f5f7ff) 0 0) padding-box, linear-gradient($gradient) border-box;

            .ti {
                color: var(--vibrant-blue-blue-500, #6689ff);
            }
        }

        &.focus:not(:disabled),
        &:hover:not(:disabled):not(.no-hover) {
            color: var(--blue-base, #2051f8);
            background: linear-gradient(var(--blue-lightest, #f5f7ff) 0 0) padding-box, linear-gradient($gradient) border-box;

            .ti {
                color: var(--blue-base, #2051f8);
            }
        }
    }

    .label-comboButton {
        &.sm {
            padding: 0 4px;
        }
        &.no-padding {
            padding: 0;
        }
        text-align: center;
        white-space: nowrap;
        text-overflow: ellipsis;
        overflow: hidden;
    }

    .vertical-separator {
        width: 1px;
        height: 100%;
        background-color: $bg-soft-200;
    }

    .filter-label {
        gap: 8px;
        display: flex;
        padding: 0 4px;
        align-items: center;
    }
    .ti {
        &.neutral {
            color: $text-main-900;
        }
        &.black {
            color: $icon-strong-900;
        }

        &.purple {
            color: $purple-base;
        }

        &.red {
            color: $red-base;
        }

        &.orange {
            color: $orange-base;
        }

        &.green {
            color: $green-base;
        }

        &.yellow {
            color: $yellow-dark;
        }
        &.blue {
            color: $blue-base;
        }
    }

    .combo-button-right-icon {
        margin-left: auto;
    }
}
</style>
